import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  CheckSquareOffset,
  Package,
  Note,
  GitDiff,
  CaretRight,
} from "@phosphor-icons/react";

function DiffPreview({ docId, a, b }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  useEffect(() => {
    let alive = true;
    api
      .get(`/documents/${docId}/diff`, { params: { a, b } })
      .then((r) => alive && setState({ loading: false, error: null, data: r.data }))
      .catch((e) =>
        alive &&
        setState({
          loading: false,
          error: formatApiError(e.response?.data?.detail) || e.message,
          data: null,
        }),
      );
    return () => {
      alive = false;
    };
  }, [docId, a, b]);

  if (state.loading) {
    return <div className="text-xs text-slate-500 font-mono">Loading diff…</div>;
  }
  if (state.error) {
    return <div className="text-xs text-red-600">Diff unavailable: {state.error}</div>;
  }
  const { added, removed, unified } = state.data || {};
  const lines = (unified || "").split("\n").slice(0, 80);
  return (
    <div data-testid={`inline-diff-${docId}`}>
      <div className="flex items-center gap-3 text-xs font-mono mb-2">
        <span className="text-emerald-700">+{added} added</span>
        <span className="text-red-700">−{removed} removed</span>
        <span className="text-slate-500">v{a} → v{b}</span>
      </div>
      <pre className="font-mono text-[11px] leading-4 bg-slate-950 text-slate-200 p-3 rounded-sm overflow-auto max-h-64">
        {lines.map((line, i) => {
          let cls = "text-slate-300";
          if (line.startsWith("+++") || line.startsWith("---")) cls = "text-cyan";
          else if (line.startsWith("@@")) cls = "text-amber-400";
          else if (line.startsWith("+")) cls = "text-emerald-400";
          else if (line.startsWith("-")) cls = "text-red-400";
          return (
            <div key={i} className={cls}>
              {line || " "}
            </div>
          );
        })}
        {(unified || "").split("\n").length > 80 && (
          <div className="text-slate-500 italic mt-1">… (truncated — open document for full diff)</div>
        )}
      </pre>
    </div>
  );
}

export default function Approvals() {
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const canBundle = ["admin", "manager"].includes(user?.role);

  useEffect(() => {
    api.get("/approvals/pending").then((r) => {
      setDocs(r.data);
      setLoading(false);
    });
  }, []);

  const downloadBundle = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/audit-bundle", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 15);
      a.download = `audit-bundle-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Audit bundle downloaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setDownloading(false);
    }
  };

  const currentNotes = (d) => {
    const v = (d.versions || []).find((x) => x.version === d.current_version);
    return v?.revision_notes || null;
  };

  const toggle = (id) => setExpanded((m) => ({ ...m, [id]: !m[id] }));

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-caps">Workflow</div>
          <h2 className="font-display text-3xl text-navy uppercase mt-1">Pending approvals</h2>
        </div>
        {canBundle && (
          <button
            onClick={downloadBundle}
            disabled={downloading}
            data-testid="download-audit-bundle"
            className="btn-outline-navy flex items-center gap-2"
          >
            <Package size={16} />
            {downloading ? "Preparing…" : "Audit bundle ZIP"}
          </button>
        )}
      </div>

      {canBundle && (
        <div className="card-flat p-4 border-l-4 border-cyan text-sm text-slate-700 flex items-start gap-3">
          <Package size={20} className="text-cyan mt-0.5 shrink-0" weight="duotone" />
          <div>
            <strong className="text-navy">One-click ISO audit bundle.</strong> Downloads a ZIP containing the latest
            approved version of every document plus <span className="font-mono">manifest.json</span> and{" "}
            <span className="font-mono">summary.csv</span> with revision log, approvals, and signatures.
          </div>
        </div>
      )}

      {loading ? (
        <div className="card-flat p-10 text-center text-sm text-slate-500">Loading…</div>
      ) : docs.length === 0 ? (
        <div className="card-flat p-12 text-center text-slate-500">
          <CheckSquareOffset size={32} className="mx-auto mb-3 text-emerald-500" />
          <div className="font-medium text-navy">Nothing waiting for review.</div>
          <div className="text-sm mt-1">All submitted documents have been approved or rejected.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {docs.map((d) => {
            const notes = currentNotes(d);
            const isRevision = d.current_version >= 2;
            const isOpen = !!expanded[d.id];
            return (
              <div
                key={d.id}
                className="card-flat"
                data-testid={`pending-${d.id}`}
              >
                <div className="p-4 flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <Link
                        to={`/documents/${d.id}`}
                        className="font-semibold text-navy hover:text-cyan transition-colors truncate"
                      >
                        {d.title}
                      </Link>
                      <span className="font-mono text-xs px-1.5 py-0.5 bg-zinc-100 border border-zinc-200 rounded-sm text-zinc-700">
                        v{d.current_version}
                      </span>
                      {isRevision ? (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 bg-amber-50 border border-amber-200 rounded-sm text-amber-800">
                          <GitDiff size={10} /> revision
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 bg-emerald-50 border border-emerald-200 rounded-sm text-emerald-800">
                          new
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-zinc-500 flex items-center gap-2 flex-wrap">
                      <span>Uploaded by {d.created_by_name}</span>
                      <span>·</span>
                      <span className="font-mono">{new Date(d.updated_at).toLocaleString()}</span>
                    </div>
                    {notes && (
                      <div
                        data-testid={`notes-${d.id}`}
                        className="mt-3 flex gap-2 rounded-sm border-l-2 border-cyan/50 bg-cyan/5 pl-3 pr-2 py-2"
                      >
                        <Note size={14} className="text-cyan mt-0.5 shrink-0" weight="fill" />
                        <p className="text-sm text-zinc-800 italic whitespace-pre-wrap">
                          &ldquo;{notes}&rdquo;
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2 shrink-0">
                    {isRevision && (
                      <button
                        onClick={() => toggle(d.id)}
                        data-testid={`toggle-diff-${d.id}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 border border-zinc-300 rounded-sm hover:bg-zinc-50 text-navy"
                      >
                        <CaretRight
                          size={12}
                          className={`transition-transform ${isOpen ? "rotate-90" : ""}`}
                        />
                        {isOpen ? "Hide" : "Preview"} diff
                      </button>
                    )}
                    <Link
                      to={`/documents/${d.id}`}
                      data-testid={`review-${d.id}`}
                      className="btn-navy text-xs px-3 py-1.5"
                    >
                      Review →
                    </Link>
                  </div>
                </div>
                {isRevision && isOpen && (
                  <div className="border-t border-zinc-200 p-4 bg-zinc-50/50">
                    <DiffPreview docId={d.id} a={d.current_version - 1} b={d.current_version} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
